import { Link } from 'react-router-dom';
import { Card, Badge, Toggle, MultiSelect, TagsInput } from '../shared';
import ContextEditingSettings from './ContextEditingSettings';
import PreloadBlock from './PreloadBlock';

const capabilityOn = (dict) => dict == null || dict.enabled !== false;

const toggleCapability = (dict, enabled) => ({ ...(dict || {}), enabled });

const patchCapability = (dict, patch) => ({ ...(dict || {}), ...patch });

export default function TabCapabilities({
    formData, handleChange, isCreate,
    knowledgeItems, availableSkills, availableSubagents, availableReminders,
    availableTools = [], agentDefaults = {}, loadingOptions,
}) {
    const capabilityDefaults = agentDefaults.capabilities || {};
    const skills = formData.skills || {};
    const skillsInclude = skills.include || [];
    const skillsExclude = skills.exclude || [];
    const skillsIncludeTags = skills.include_tags || [];
    const skillsExcludeTags = skills.exclude_tags || [];
    const skillsPreload = skills.preload;

    const availableSkillTags = Array.from(
        new Set((availableSkills || []).flatMap((s) => s.tags || []))
    ).sort().map((t) => ({ value: t, label: `#${t}` }));

    const patchSkills = (patch) => handleChange('skills', patchCapability(skills, patch));

    return (
        <div className="space-y-4">
            <Card title={<span className="flex items-center gap-2">Skills <Toggle checked={capabilityOn(skills)} onChange={(val) => patchSkills({ enabled: val })} size="sm" /></span>}>
                {capabilityOn(skills) && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <MultiSelect
                                label="Include" hint="Whitelist skill ids. Leave empty + no Include tags = allow all."
                                options={availableSkills}
                                selected={skillsInclude}
                                onChange={(val) => patchSkills({ include: val })}
                                placeholder="All skills..."
                                loading={loadingOptions}
                            />
                            <MultiSelect
                                label="Exclude" hint="Blocklist — always wins over include."
                                options={availableSkills}
                                selected={skillsExclude}
                                onChange={(val) => patchSkills({ exclude: val })}
                                placeholder="None excluded..."
                                loading={loadingOptions}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <MultiSelect
                                label="Include tags" hint="Pick tags from existing skills — any matching tag grants access."
                                options={availableSkillTags}
                                selected={skillsIncludeTags}
                                onChange={(val) => patchSkills({ include_tags: val })}
                                placeholder="No tag filter..."
                                loading={loadingOptions}
                            />
                            <MultiSelect
                                label="Exclude tags" hint="Skills carrying any of these tags are blocked."
                                options={availableSkillTags}
                                selected={skillsExcludeTags}
                                onChange={(val) => patchSkills({ exclude_tags: val })}
                                placeholder="No tag block..."
                                loading={loadingOptions}
                            />
                        </div>
                        <PreloadBlock
                            preload={skillsPreload}
                            onPreloadChange={(val) => patchSkills({ preload: val })}
                            mention={skills.mention}
                            onMentionChange={(val) => patchSkills({ mention: val })}
                            options={availableSkills}
                            include={skillsInclude}
                            exclude={skillsExclude}
                            includeTags={skillsIncludeTags}
                            excludeTags={skillsExcludeTags}
                            loading={loadingOptions}
                            resourceLabel="skill"
                            listPath="/skills/"
                            groupName="skills_preload"
                        />
                    </div>
                )}
            </Card>

            <Card title={<span className="flex items-center gap-2">Subagents <Toggle checked={capabilityOn(formData.subagents)} onChange={(val) => handleChange('subagents', toggleCapability(formData.subagents, val))} size="sm" /></span>}>
                {capabilityOn(formData.subagents) && (() => {
                    const subagents = formData.subagents || {};
                    const patchSubagents = (patch) => handleChange('subagents', patchCapability(formData.subagents, patch));
                    const availableSubagentTags = Array.from(
                        new Set((availableSubagents || []).flatMap((s) => s.tags || []))
                    ).sort().map((t) => ({ value: t, label: `#${t}` }));
                    return (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <MultiSelect
                                    label="Include"
                                    hint="Whitelist subagent ids. Leave empty + no include tags = allow all."
                                    options={availableSubagents}
                                    selected={subagents.include || []}
                                    onChange={(val) => patchSubagents({ include: val })}
                                    placeholder="All subagents..."
                                    loading={loadingOptions}
                                />
                                <MultiSelect
                                    label="Exclude"
                                    hint="Blocklist — always wins over include."
                                    options={availableSubagents}
                                    selected={subagents.exclude || []}
                                    onChange={(val) => patchSubagents({ exclude: val })}
                                    placeholder="None excluded..."
                                    loading={loadingOptions}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <MultiSelect
                                    label="Include tags"
                                    hint="Pick tags from existing subagents — any matching tag grants access."
                                    options={availableSubagentTags}
                                    selected={subagents.include_tags || []}
                                    onChange={(val) => patchSubagents({ include_tags: val })}
                                    placeholder="No tag filter..."
                                    loading={loadingOptions}
                                />
                                <MultiSelect
                                    label="Exclude tags"
                                    hint="Subagents carrying any of these tags are blocked."
                                    options={availableSubagentTags}
                                    selected={subagents.exclude_tags || []}
                                    onChange={(val) => patchSubagents({ exclude_tags: val })}
                                    placeholder="No tag block..."
                                    loading={loadingOptions}
                                />
                            </div>
                            <PreloadBlock
                                preload={subagents.preload}
                                onPreloadChange={(val) => patchSubagents({ preload: val })}
                                mention={subagents.mention}
                                onMentionChange={(val) => patchSubagents({ mention: val })}
                                options={availableSubagents}
                                include={subagents.include || []}
                                exclude={subagents.exclude || []}
                                includeTags={subagents.include_tags || []}
                                excludeTags={subagents.exclude_tags || []}
                                loading={loadingOptions}
                                resourceLabel="subagent"
                                listPath="/subagents/"
                                groupName="subagents_preload"
                            />
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <Toggle
                                    checked={subagents.stream_subgraphs !== false}
                                    onChange={(val) => patchSubagents({ stream_subgraphs: val })}
                                    size="sm"
                                />
                                <span>Stream subgraphs</span>
                                <span className="text-xs text-gray-500">— relay subagent events to client in real-time</span>
                            </label>
                        </div>
                    );
                })()}
            </Card>

            <Card title={<span className="flex items-center gap-2">Reminders <Toggle checked={formData.reminders_enabled !== false} onChange={(val) => handleChange('reminders_enabled', val)} size="sm" /></span>}>
                {formData.reminders_enabled !== false && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <MultiSelect
                                label="Include"
                                options={availableReminders}
                                selected={formData.reminders_include || []}
                                onChange={(val) => handleChange('reminders_include', val.length > 0 ? val : null)}
                                placeholder="All reminders..."
                                loading={loadingOptions}
                            />
                            <MultiSelect
                                label="Exclude"
                                options={availableReminders}
                                selected={formData.reminders_exclude || []}
                                onChange={(val) => handleChange('reminders_exclude', val.length > 0 ? val : null)}
                                placeholder="Exclude reminders..."
                                loading={loadingOptions}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max per turn</label>
                            <input
                                type="number"
                                className="w-24 px-3 py-1.5 text-sm border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                value={formData.reminders_max_per_turn ?? 5}
                                onChange={(e) => handleChange('reminders_max_per_turn', parseInt(e.target.value) || 5)}
                                min={1}
                                max={20}
                            />
                        </div>
                    </div>
                )}
            </Card>

            <Card
                title={<span className="flex items-center gap-2">Knowledge <Toggle checked={capabilityOn(formData.knowledge)} onChange={(val) => handleChange('knowledge', toggleCapability(formData.knowledge, val))} size="sm" /></span>}
                headerRight={<Link to="/admin/knowledge" className="text-xs text-blue-600 hover:text-blue-800">Manage Knowledge</Link>}
            >
                {capabilityOn(formData.knowledge) && (() => {
                    const knowledge = formData.knowledge || {};
                    const patchKnowledge = (patch) => handleChange('knowledge', patchCapability(formData.knowledge, patch));
                    const availableKnowledgeOptions = (knowledgeItems || []).map((k) => ({ value: k.id, label: k.name || k.topic || k.id, tags: k.tags || [] }));
                    const availableKnowledgeTags = Array.from(
                        new Set((knowledgeItems || []).flatMap((k) => k.tags || []))
                    ).sort().map((t) => ({ value: t, label: `#${t}` }));
                    return (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <MultiSelect
                                    label="Include"
                                    hint="Whitelist knowledge ids. Leave empty + no include tags = allow all."
                                    options={availableKnowledgeOptions}
                                    selected={knowledge.include || []}
                                    onChange={(val) => patchKnowledge({ include: val })}
                                    placeholder="All knowledge..."
                                    loading={loadingOptions}
                                />
                                <MultiSelect
                                    label="Exclude"
                                    hint="Blocklist — always wins over include."
                                    options={availableKnowledgeOptions}
                                    selected={knowledge.exclude || []}
                                    onChange={(val) => patchKnowledge({ exclude: val })}
                                    placeholder="None excluded..."
                                    loading={loadingOptions}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <MultiSelect
                                    label="Include tags"
                                    hint="Pick tags from existing knowledge — any matching tag grants access."
                                    options={availableKnowledgeTags}
                                    selected={knowledge.include_tags || []}
                                    onChange={(val) => patchKnowledge({ include_tags: val })}
                                    placeholder="No tag filter..."
                                    loading={loadingOptions}
                                />
                                <MultiSelect
                                    label="Exclude tags"
                                    hint="Knowledge carrying any of these tags is blocked."
                                    options={availableKnowledgeTags}
                                    selected={knowledge.exclude_tags || []}
                                    onChange={(val) => patchKnowledge({ exclude_tags: val })}
                                    placeholder="No tag block..."
                                    loading={loadingOptions}
                                />
                            </div>
                            <PreloadBlock
                                preload={knowledge.preload}
                                onPreloadChange={(val) => patchKnowledge({ preload: val })}
                                mention={knowledge.mention}
                                onMentionChange={(val) => patchKnowledge({ mention: val })}
                                options={availableKnowledgeOptions}
                                include={knowledge.include || []}
                                exclude={knowledge.exclude || []}
                                includeTags={knowledge.include_tags || []}
                                excludeTags={knowledge.exclude_tags || []}
                                loading={loadingOptions}
                                resourceLabel="knowledge"
                                listPath="/knowledge/"
                                groupName="knowledge_preload"
                            />
                        </div>
                    );
                })()}
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    Context Editing
                    <Toggle
                        checked={capabilityOn(formData.context_editing)}
                        onChange={(val) => handleChange('context_editing', toggleCapability(formData.context_editing, val))}
                        size="sm"
                    />
                </span>
            }>
                {capabilityOn(formData.context_editing) ? (
                    <ContextEditingSettings
                        value={formData.context_editing || {}}
                        defaults={capabilityDefaults.context_editing || {}}
                        availableTools={availableTools}
                        onChange={(next) => handleChange('context_editing', { ...next, enabled: true })}
                    />
                ) : (
                    <p className="text-xs text-gray-500">
                        Older tool calls are kept verbatim in state. The agent may run out of context on long sessions.
                    </p>
                )}
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    Long-term Memory
                    <Toggle
                        checked={capabilityOn(formData.long_term_memory)}
                        onChange={(val) => handleChange('long_term_memory', toggleCapability(formData.long_term_memory, val))}
                        size="sm"
                    />
                </span>
            }>
                <p className="text-xs text-gray-500">
                    Injects the long-term memory system prompt so the agent can read/write <code className="px-1 py-0.5 bg-gray-100 rounded">/memories/*</code>.
                </p>
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    Image Search
                    <Toggle
                        checked={capabilityOn(formData.image_search)}
                        onChange={(val) => handleChange('image_search', toggleCapability(formData.image_search, val))}
                        size="sm"
                    />
                </span>
            }>
                <p className="text-xs text-gray-500">
                    Enables the image search guidance for the web search tool.
                </p>
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    Visualizer
                    <Toggle
                        checked={capabilityOn(formData.visualizer)}
                        onChange={(val) => handleChange('visualizer', toggleCapability(formData.visualizer, val))}
                        size="sm"
                    />
                </span>
            }>
                <p className="text-xs text-gray-500">
                    Enables the visualizer system prompt for <code className="px-1 py-0.5 bg-gray-100 rounded">visualize_show_chart</code> workflows.
                </p>
            </Card>

            <Card title={
                <span className="flex items-center gap-2">
                    Sandbox
                    <Toggle
                        checked={capabilityOn(formData.sandbox)}
                        onChange={(val) => handleChange('sandbox', toggleCapability(formData.sandbox, val))}
                        size="sm"
                    />
                </span>
            }>
                {capabilityOn(formData.sandbox) ? (
                    <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={formData.sandbox?.simplize_sdk !== false}
                            onChange={(e) =>
                                handleChange('sandbox', patchCapability(formData.sandbox, { simplize_sdk: e.target.checked }))
                            }
                        />
                        <span>
                            <span className="font-medium">Simplize SDK</span>
                            <span className="block text-xs text-gray-500">
                                Inject the Simplize Python SDK instructions into the sandbox prompt.
                            </span>
                        </span>
                    </label>
                ) : (
                    <p className="text-xs text-gray-500">
                        Sandbox disabled. The agent uses the S3 workspace backend only; code execution is unavailable.
                    </p>
                )}
            </Card>
        </div>
    );
}
